import { zodResolver } from "@hookform/resolvers/zod";
import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouteContext,
} from "@tanstack/react-router";
import { useAtomValue, useSetAtom } from "jotai";
import type { ReactNode, JSX } from "react";
import { useForm } from "react-hook-form";

import ArrowRight from "@repo/icons/default/ArrowRight";

import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import {
  FormControl,
  FormField,
  FormHandlerSubmit,
  FormItem,
  FormRoot,
  FormSubmitButton,
} from "@/components/ui/form/form";
import { InputNumber } from "@/components/ui/input/input-number";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { FullPageSpinner } from "@/components/ui/loading/full-page-spinner";
import {
  CardSelectItem,
  CardSelectItemContainer,
  CardSelectRoot,
  CardSelectTitle,
} from "@/components/ui/select-input/card-select";
import { useGraphQL } from "@/hooks/use-graphql";
import { firstScheduleAtom } from "@/lib/atoms/first-schedule-atom";
import { AppointmentTypeCategoryEnum } from "@/lib/enums/appointment-type-category-enum";
import { AppointmentTypeModalityEnum } from "@/lib/enums/appointment-type-modality-enum";
import {
  FOLLOW_UP_CUSTOM_DAYS_OPTION,
  FOLLOW_UP_DAYS_OPTIONS,
  type FollowUpDaysFormFields,
  followUpDaysFormSchema,
  toFollowUpDays,
} from "@/lib/form-schemas/first-schedule-appointment-types-schema";
import {
  FIRST_SCHEDULE_STEPS,
  FOLLOW_UP_APPOINTMENT_TYPE_NAME,
  getFirstScheduleProgress,
} from "@/lib/helpers/first-schedule-flow";
import { requireFreeFollowUpDraft } from "@/lib/helpers/first-schedule-guards";
import { FirstScheduleFollowUpQuery } from "@/routes/onboarding/first-schedule/follow-up";

const toDaysFormValues = (
  days: number | null | undefined,
): Partial<FollowUpDaysFormFields> => {
  if (!days) return {};

  const option = String(days);
  return (FOLLOW_UP_DAYS_OPTIONS as readonly string[]).includes(option)
    ? { daysOption: option }
    : { daysOption: FOLLOW_UP_CUSTOM_DAYS_OPTION, customDays: option };
};

const FirstScheduleFollowUpDaysPage = (): JSX.Element => {
  const { user } = useRouteContext({ from: "__root__" });
  const form = useAtomValue(firstScheduleAtom);

  const { data, isPending } = useGraphQL(FirstScheduleFollowUpQuery, {
    codUsuario: user.codUsuario,
    name: FOLLOW_UP_APPOINTMENT_TYPE_NAME,
    category: AppointmentTypeCategoryEnum.Manual,
    modality: AppointmentTypeModalityEnum.Presencial,
  });

  if (isPending) {
    return (
      <FollowUpDaysLayout>
        <FullPageSpinner />
      </FollowUpDaysLayout>
    );
  }

  const currentDays =
    data?.usuarios[0]?.professionalData?.appointmentPreferences[0]?.retornoGratuitoEmDias;

  return (
    <FollowUpDaysLayout>
      <FollowUpDaysForm defaultDays={form.followUp?.daysToFreeFollowUp ?? currentDays} />
    </FollowUpDaysLayout>
  );
};

const FollowUpDaysLayout = ({ children }: { children: ReactNode }): JSX.Element => {
  const { flags } = useRouteContext({ from: "__root__" });

  return (
    <RoundedLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar
          progress={getFirstScheduleProgress(
            FIRST_SCHEDULE_STEPS.followUpDays,
            flags["exibe-configuracao-tipos-de-atendimento-na-primeira-agenda"],
          )}
        />
      </FlowHeader>

      <Page className="flex flex-col pb-0 px-6" showCustomerIoContainer={false}>
        {children}
      </Page>
    </RoundedLayout>
  );
};

interface FollowUpDaysFormProps {
  defaultDays: number | null | undefined;
}

const FollowUpDaysForm = ({ defaultDays }: FollowUpDaysFormProps): JSX.Element => {
  const navigate = useNavigate();
  const setDraft = useSetAtom(firstScheduleAtom);

  const formMethods = useForm<FollowUpDaysFormFields>({
    resolver: zodResolver(followUpDaysFormSchema),
    defaultValues: toDaysFormValues(defaultDays),
  });

  const selectedDaysOption = formMethods.watch("daysOption");

  const handleSubmit = (values: FollowUpDaysFormFields): void => {
    setDraft((prev) => ({
      ...prev,
      followUp: prev.followUp
        ? { ...prev.followUp, daysToFreeFollowUp: toFollowUpDays(values) }
        : null,
    }));

    void navigate({ to: "/onboarding/first-schedule/preview" });
  };

  return (
    <FormRoot {...formMethods}>
      <FormHandlerSubmit
        handleSubmit={handleSubmit}
        className="w-full flex-1 justify-between gap-4"
      >
        <div className="flex flex-col gap-4">
          <CardSelectTitle>Em até quantos dias o retorno é gratuito?</CardSelectTitle>

          <FormField
            control={formMethods.control}
            name="daysOption"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div>
                    <CardSelectRoot
                      name="follow-up-days"
                      defaultValue={field.value}
                      onChange={field.onChange}
                    >
                      <CardSelectItemContainer>
                        {FOLLOW_UP_DAYS_OPTIONS.map((days) => (
                          <CardSelectItem key={days} value={days}>
                            {days} dias
                          </CardSelectItem>
                        ))}
                        <CardSelectItem value={FOLLOW_UP_CUSTOM_DAYS_OPTION}>
                          Outro
                        </CardSelectItem>
                      </CardSelectItemContainer>
                    </CardSelectRoot>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          {selectedDaysOption === FOLLOW_UP_CUSTOM_DAYS_OPTION ? (
            <FormField
              control={formMethods.control}
              name="customDays"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputNumber
                      title="Quantidade de dias"
                      min="1"
                      max="999"
                      placeholder="De 1 a 999"
                      showArrowsControl={false}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          ) : null}
        </div>

        <div className="sticky bottom-0 bg-white pt-4 pb-4">
          <FormSubmitButton color="secondary">
            <div className="flex flex-row items-center justify-center gap-2">
              Continuar
              <ArrowRight size={24} aria-hidden="true" />
            </div>
          </FormSubmitButton>
        </div>
      </FormHandlerSubmit>
    </FormRoot>
  );
};

export const Route = createFileRoute("/onboarding/first-schedule/follow-up-days")({
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-configuracao-tipos-de-atendimento-na-primeira-agenda"]) {
      redirect({ to: "/onboarding/first-schedule/success", throw: true });
    }
    requireFreeFollowUpDraft();
  },
  component: FirstScheduleFollowUpDaysPage,
});
